import React, { useState, useEffect } from "react";
import styled from "styled-components";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const PageWrapper = styled.div`
  padding: 2rem;
  background: #f9fafb;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(23, 31, 114, 0.08);
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  text-align: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  th, td {
    border: 1px solid #ddd;
    padding: 0.75rem;
    text-align: center;
  }

  th {
    background: #f1f1f1;
  }
`;

const RegistroTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  th, td {
    border: 1px solid #ccc;
    padding: 0.5rem;
    text-align: center;
  }

  th {
    background: #2e186a;
    color: white;
  }
`;

const Button = styled.button`
  background: #2e186a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: #4b2ebf;
  }
`;


interface Empleado {
  id: number;
  nombre: string;
  apellidos: string;
  usuario: string;
}

interface Registro {
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  incidencia: string;
}

interface HorasSemana {
  horas_lunes: string;
  horas_martes: string;
  horas_miercoles: string;
  horas_jueves: string;
  horas_viernes: string;
  horas_sabado: string;
  horas_domingo: string;
}

const Admin: React.FC = () => {
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [selectedEmpleado, setSelectedEmpleado] = useState<Empleado | null>(null);

  const [showHorario, setShowHorario] = useState(false);
  const [horasSemana, setHorasSemana] = useState<HorasSemana | null>(null);


  useEffect(() => {
    fetch("https://kevhel.com/api/get_trabajadores.php", {
      credentials: "include",
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setEmpleados(data.trabajadores);
      });
  }, []);

  const fetchRegistros = async (emp: Empleado) => {
    setSelectedEmpleado(emp);
    setShowHorario(false);

    const res = await fetch(
      `https://kevhel.com/api/get_registros.php?user_id=${emp.id}`,
      { credentials: "include" }
    );
    const data = await res.json();
    setRegistros(data.success ? data.registros : []);
  };

  const fetchHorario = async (emp: Empleado) => {
    setSelectedEmpleado(emp);
    setRegistros([]);
    setShowHorario(true);

    const res = await fetch(
      `https://kevhel.com/api/get_horas_empleado.php?user_id=${emp.id}`,
      { credentials: "include" }
    );
    const data = await res.json();

    if (data.success && data.horas) {
      setHorasSemana(data.horas); 
    } else {
      alert("No hay horario para este empleado");
    }
  };

  const guardarHorario = async () => {
    if (!selectedEmpleado || !horasSemana) return;

    await fetch("https://kevhel.com/api/update_horas_empleado.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        empleado_id: selectedEmpleado.id,
        ...horasSemana,
      }),
    });

    alert("Horario actualizado correctamente");
  };


  const formatDate = (date: string) => {
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  };

  const downloadPDF = () => {
    if (!selectedEmpleado) return;

    const doc = new jsPDF();
    doc.text(
      `Registros de ${selectedEmpleado.nombre} ${selectedEmpleado.apellidos}`,
      14,
      15
    );

    autoTable(doc, {
      head: [["Fecha", "Inicio", "Fin", "Incidencia"]],
      body: registros.map(r => [
        formatDate(r.fecha),
        r.hora_inicio,
        r.hora_fin,
        r.incidencia || "-",
      ]),
      startY: 25,
    });

    doc.save(`Registros_${selectedEmpleado.nombre}.pdf`);
  };

  return (
    <PageWrapper>
      <Card>
        <Title>Gestión de empleados</Title>

        <Table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Apellidos</th>
              <th>Usuario</th>
              <th>Acciones</th>
              <th>Horario</th>
            </tr>
          </thead>
          <tbody>
            {empleados.map(emp => (
              <tr key={emp.id}>
                <td>{emp.nombre}</td>
                <td>{emp.apellidos}</td>
                <td>{emp.usuario}</td>
                <td>
                  <Button onClick={() => fetchRegistros(emp)}> 
                    Ver registros 
                  </Button> 
                </td> 
                <td> 
                  <Button onClick={() => fetchHorario(emp)}> 
                    Ver horario 
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {showHorario && selectedEmpleado && horasSemana && (
        <Card>
          <Title>
            Horario de {selectedEmpleado.nombre} {selectedEmpleado.apellidos}
          </Title>

          <RegistroTable>
            <thead>
              <tr>
                <th>L</th><th>M</th><th>X</th><th>J</th>
                <th>V</th><th>S</th><th>D</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {Object.entries(horasSemana).map(([dia, valor]) => (
                  <td key={dia}>
                    <input
                      type="time"
                      value={valor}
                      onChange={e =>
                        setHorasSemana({
                          ...horasSemana,
                          [dia]: e.target.value,
                        })
                      }
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </RegistroTable>

          <Button style={{ marginTop: "1rem" }} onClick={guardarHorario}>Guardar horario</Button>
        </Card>
      )}

      {!showHorario && selectedEmpleado && registros.length > 0 && (
        <Card>
          <Title>Registros</Title>

          <RegistroTable>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Incidencia</th>
              </tr>
            </thead>
            <tbody>
              {registros.map((r, i) => (
                <tr key={i}>
                  <td>{formatDate(r.fecha)}</td>
                  <td>{r.hora_inicio}</td>
                  <td>{r.hora_fin}</td>
                  <td>{r.incidencia || "-"}</td>
                </tr>
              ))}
            </tbody>
          </RegistroTable>

          <Button style={{ marginTop: "1rem" }} onClick={downloadPDF}>Descargar PDF</Button>
        </Card>
      )}
    </PageWrapper>
  );
};

export default Admin;
